/**
 * User Center Frontend - Main App Component
 */

import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { LoadingFallback } from '@components/LoadingFallback';
import { ProtectedRoute, PublicRoute, AdminProtectedRoute, AdminPublicRoute } from '@lib/routes';
import { authUtils } from '@lib/api';
import { ReturnToAppBanner } from '@components/ReturnToAppBanner';
import OAuthCallback from '@pages/OAuthCallback';

// Public pages
const Login = lazy(() => import('@pages/Login'));
const Register = lazy(() => import('@pages/Register'));
const CompleteSignup = lazy(() => import('@pages/CompleteSignup'));
const Consent = lazy(() => import('@pages/Consent'));
const AgreementView = lazy(() => import('@pages/AgreementView'));

// Protected pages
const Identities = lazy(() => import('@pages/Identities'));
const BindPhone = lazy(() => import('@pages/BindPhone'));

// Admin pages - conditionally loaded
const AdminLogin = lazy(() => import('@pages/admin/AdminLogin'));
const UserManagement = lazy(() => import('@pages/admin/UserManagement'));
const PlatformManagement = lazy(() => import('@pages/admin/PlatformManagement'));
const AgreementManagement = lazy(() => import('@pages/admin/AgreementManagement'));
const SDKDocs = lazy(() => import('@pages/admin/SDKDocs'));
const SMSSettings = lazy(() => import('@pages/admin/SMSSettings'));

function App() {
  // Auto-refresh token before it expires (check every 4 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      if (authUtils.isAuthenticated() && authUtils.isTokenExpiringSoon()) {
        authUtils.refreshAccessToken().then(ok => {
          if (!ok) {
            authUtils.logout();
            window.location.href = "/login";
          }
        });
      }
    }, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <ErrorBoundary>
      <div className="min-h-screen">
        <ReturnToAppBanner />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/complete-signup" element={<PublicRoute><CompleteSignup /></PublicRoute>} />
            <Route path="/consent" element={<Consent />} />
            <Route path="/oauth-callback" element={<OAuthCallback />} />
            <Route path="/agreement/:id" element={<AgreementView />} />

            {/* Admin routes - /admin/login redirects to main login */}
            <Route path="/admin/login" element={<AdminPublicRoute><AdminLogin /></AdminPublicRoute>} />
            <Route path="/admin/users" element={
              <AdminProtectedRoute>
                <UserManagement />
              </AdminProtectedRoute>
            } />
            <Route path="/admin/platform" element={
              <AdminProtectedRoute>
                <PlatformManagement />
              </AdminProtectedRoute>
            } />
            <Route path="/admin/agreements" element={
              <AdminProtectedRoute>
                <AgreementManagement />
              </AdminProtectedRoute>
            } />
            <Route path="/admin/sdk" element={
              <AdminProtectedRoute>
                <SDKDocs />
              </AdminProtectedRoute>
            } />
            <Route path="/admin/sms" element={
              <AdminProtectedRoute>
                <SMSSettings />
              </AdminProtectedRoute>
            } />
            <Route path="/admin" element={<Navigate to="/admin/users" replace />} />

            {/* Protected routes */}
            <Route path="/*" element={
              <ProtectedRoute>
                <Routes>
                  <Route path="/" element={<Navigate to="/identities" replace />} />
                  <Route path="/identities" element={<Identities />} />
                  <Route path="/bind-phone" element={<BindPhone />} />
                  <Route path="*" element={<Navigate to="/identities" replace />} />
                </Routes>
              </ProtectedRoute>
            } />
          </Routes>
        </Suspense>
      </div>
    </ErrorBoundary>
  );
}

export default App;
